/**
 * Scale Calibration Store (Gap 7)
 *
 * Manages the S4 calibration step: a facilitator opens calibration on a
 * criterion's scale (with anchor definitions), participants record whether they
 * read the scale the same way or differently, and the facilitator marks it
 * calibrated. Supabase is the source of truth.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  isSupabaseAvailable,
  generateUUID,
} from '../../lib/supabaseSync';
import type {
  ScaleCalibration,
  ScaleCalibrationAck,
  CalibrationAnchor,
  CalibrationStatus,
  AnchorAlignment,
} from '../../types/prioritization/scaleCalibration';

const CAL_TABLE = 'scale_calibrations';
const ACK_TABLE = 'scale_calibration_acks';

function rowToCalibration(row: Record<string, unknown>): ScaleCalibration {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    workspaceId: row.workspace_id as string,
    attributeKey: row.attribute_key as string,
    attributeLabel: (row.attribute_label as string) ?? '',
    scaleId: (row.scale_id as string | null) ?? null,
    scaleLabel: (row.scale_label as string) ?? '',
    anchors: (row.anchors as CalibrationAnchor[]) ?? [],
    status: (row.status as CalibrationStatus) ?? 'draft',
    openedBy: (row.opened_by as string | null) ?? null,
    openedAt: (row.opened_at as string | null) ?? null,
    calibratedBy: (row.calibrated_by as string | null) ?? null,
    calibratedAt: (row.calibrated_at as string | null) ?? null,
    createdAt: (row.created_at as string) ?? new Date().toISOString(),
    updatedAt: (row.updated_at as string) ?? new Date().toISOString(),
  };
}

function rowToAck(row: Record<string, unknown>): ScaleCalibrationAck {
  return {
    id: row.id as string,
    calibrationId: row.calibration_id as string,
    workspaceId: row.workspace_id as string,
    participantId: row.participant_id as string,
    participantName: (row.participant_name as string) ?? '',
    alignment: (row.alignment as AnchorAlignment) ?? 'aligned',
    note: (row.note as string | null) ?? null,
    createdAt: (row.created_at as string) ?? new Date().toISOString(),
    updatedAt: (row.updated_at as string) ?? new Date().toISOString(),
  };
}

interface ScaleCalibrationState {
  calibrations: ScaleCalibration[];
  acks: ScaleCalibrationAck[];
  isLoading: boolean;
  error: string | null;

  fetchForSession: (sessionId: string) => Promise<void>;

  /** Create or update a calibration's anchor definitions (facilitator). */
  upsertCalibration: (params: {
    sessionId: string;
    attributeKey: string;
    attributeLabel: string;
    scaleId?: string | null;
    scaleLabel: string;
    anchors: CalibrationAnchor[];
    status?: CalibrationStatus;
    actorId: string;
  }) => Promise<{ calibration?: ScaleCalibration; error?: string }>;

  setCalibrationStatus: (params: {
    calibrationId: string;
    status: CalibrationStatus;
    actorId: string;
  }) => Promise<{ error?: string }>;

  /** Participant records their reading of the scale. */
  acknowledge: (params: {
    calibrationId: string;
    participantId: string;
    participantName: string;
    alignment: AnchorAlignment;
    note?: string;
  }) => Promise<{ error?: string }>;

  getCalibrationsForSession: (sessionId: string) => ScaleCalibration[];
  getAcksForCalibration: (calibrationId: string) => ScaleCalibrationAck[];
}

export const useScaleCalibrationStore = create<ScaleCalibrationState>()((set, get) => ({
  calibrations: [],
  acks: [],
  isLoading: false,
  error: null,

  fetchForSession: async (sessionId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    set({ isLoading: true, error: null });

    const { data: calRows, error: calErr } = await supabase
      .from(CAL_TABLE)
      .select('*')
      .eq('session_id', sessionId);
    if (calErr) {
      set({ isLoading: false, error: calErr.message });
      return;
    }
    const cals = (calRows ?? []).map(r => rowToCalibration(r as Record<string, unknown>));
    const calIds = cals.map(c => c.id);

    let acks: ScaleCalibrationAck[] = [];
    if (calIds.length > 0) {
      const { data: ackRows } = await supabase
        .from(ACK_TABLE)
        .select('*')
        .in('calibration_id', calIds);
      acks = (ackRows ?? []).map(r => rowToAck(r as Record<string, unknown>));
    }

    set(state => ({
      calibrations: [...state.calibrations.filter(c => c.sessionId !== sessionId), ...cals],
      acks: [...state.acks.filter(a => !calIds.includes(a.calibrationId)), ...acks],
      isLoading: false,
    }));
  },

  upsertCalibration: async ({ sessionId, attributeKey, attributeLabel, scaleId, scaleLabel, anchors, status, actorId }) => {
    if (!isSupabaseAvailable()) return { error: 'Database not available.' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { error: 'Database client not available.' };
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return { error: 'No workspace context.' };

    const existing = get().calibrations.find(
      c => c.sessionId === sessionId && c.attributeKey === attributeKey && (c.scaleId ?? '') === (scaleId ?? '')
    );

    const now = new Date().toISOString();
    const nextStatus = status ?? existing?.status ?? 'draft';
    const id = existing?.id ?? generateUUID();

    const record: Record<string, unknown> = {
      id,
      session_id: sessionId,
      workspace_id: workspaceId,
      attribute_key: attributeKey,
      attribute_label: attributeLabel,
      scale_id: scaleId ?? null,
      scale_label: scaleLabel,
      anchors,
      status: nextStatus,
      updated_at: now,
    };
    if (nextStatus === 'open' && !existing?.openedAt) {
      record.opened_by = actorId;
      record.opened_at = now;
    }
    if (!existing) record.created_at = now;

    const { error } = await supabase
      .from(CAL_TABLE)
      .upsert(record, { onConflict: 'id' });
    if (error) return { error: error.message };

    const calibration: ScaleCalibration = {
      id,
      sessionId,
      workspaceId,
      attributeKey,
      attributeLabel,
      scaleId: scaleId ?? null,
      scaleLabel,
      anchors,
      status: nextStatus,
      openedBy: (record.opened_by as string) ?? existing?.openedBy ?? null,
      openedAt: (record.opened_at as string) ?? existing?.openedAt ?? null,
      calibratedBy: existing?.calibratedBy ?? null,
      calibratedAt: existing?.calibratedAt ?? null,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };

    set(state => ({
      calibrations: [...state.calibrations.filter(c => c.id !== id), calibration],
    }));
    return { calibration };
  },

  setCalibrationStatus: async ({ calibrationId, status, actorId }) => {
    if (!isSupabaseAvailable()) return { error: 'Database not available.' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { error: 'Database client not available.' };

    const now = new Date().toISOString();
    const patch: Record<string, unknown> = { status, updated_at: now };
    if (status === 'calibrated') {
      patch.calibrated_by = actorId;
      patch.calibrated_at = now;
    }
    if (status === 'open') {
      patch.opened_by = actorId;
      patch.opened_at = now;
    }

    const { error } = await supabase.from(CAL_TABLE).update(patch).eq('id', calibrationId);
    if (error) return { error: error.message };

    set(state => ({
      calibrations: state.calibrations.map(c =>
        c.id === calibrationId
          ? {
              ...c,
              status,
              ...(status === 'calibrated' ? { calibratedBy: actorId, calibratedAt: now } : {}),
              ...(status === 'open' ? { openedBy: actorId, openedAt: now } : {}),
              updatedAt: now,
            }
          : c
      ),
    }));
    return {};
  },

  acknowledge: async ({ calibrationId, participantId, participantName, alignment, note }) => {
    if (!isSupabaseAvailable()) return { error: 'Database not available.' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { error: 'Database client not available.' };
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return { error: 'No workspace context.' };

    const now = new Date().toISOString();
    const existing = get().acks.find(a => a.calibrationId === calibrationId && a.participantId === participantId);
    const id = existing?.id ?? generateUUID();

    const record = {
      id,
      calibration_id: calibrationId,
      workspace_id: workspaceId,
      participant_id: participantId,
      participant_name: participantName,
      alignment,
      note: note?.trim() ? note.trim() : null,
      created_at: existing?.createdAt ?? now,
      updated_at: now,
    };

    const { error } = await supabase
      .from(ACK_TABLE)
      .upsert(record, { onConflict: 'calibration_id,participant_id' });
    if (error) return { error: error.message };

    const ack: ScaleCalibrationAck = {
      id,
      calibrationId,
      workspaceId,
      participantId,
      participantName,
      alignment,
      note: note?.trim() ? note.trim() : null,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
    set(state => ({
      acks: [...state.acks.filter(a => a.id !== id), ack],
    }));
    return {};
  },

  getCalibrationsForSession: (sessionId) =>
    get().calibrations.filter(c => c.sessionId === sessionId),

  getAcksForCalibration: (calibrationId) =>
    get().acks.filter(a => a.calibrationId === calibrationId),
}));
